import { roundProjectAllocationPercents } from '@common/neo.tax';

import { getProjectAllocationsForProjectPercents } from '@neo.tax/shared';

import { ContributorAllocationsInPeriod } from '../types';

type ContributorAllocationPayload = {
  allocationPercents: { allocationPercent: number; ascProjectId: string }[];
  nonProjectPercent: number;
};

/**
 * Builds the whole-percent payload the `updateContributorAllocationsForAsc` mutation expects from the
 * modal's working allocation state.
 *
 * The percents are rounded as a group through {@link roundProjectAllocationPercents} and the
 * non-project value is taken as the residual it derives, rather than each value being rounded on its
 * own. Rounding independently lets the payload disagree with what the modal validated: the Apply gate
 * sums the unrounded values, so three projects at 33.33 read as exactly 100% on screen but submit as
 * 33 + 33 + 33 with a non-project percent of 0. The server then rejects the 99% total, and the throw
 * is outside the mutation's error handling, so the user gets a generic failure toast. Deriving the
 * residual keeps the payload reconciled to 100% for any values the gate accepts.
 *
 * A consequence worth knowing: the submitted non-project percent can differ by up to a point from the
 * unrounded value shown in that field, because it absorbs the projects' rounding.
 */
export const buildContributorAllocationPayload = (
  allocationsInPeriod: ContributorAllocationsInPeriod,
): ContributorAllocationPayload => {
  const { projectPercents } = allocationsInPeriod;
  const projectAllocations = getProjectAllocationsForProjectPercents(projectPercents);

  const { nonProjectPercent, projectPercents: roundedProjectPercents } = roundProjectAllocationPercents(
    projectAllocations.map(([, value]) => value.allocationPercent),
  );

  return {
    allocationPercents: projectAllocations.map(([, value], index) => ({
      ascProjectId: value.projectId,
      allocationPercent: roundedProjectPercents[index],
    })),
    nonProjectPercent,
  };
};
